<template>
    <app-layout>
        <common-page-plain>
            <common-content-spacer height="medium" :isCenter="true">
                <common-page-header
                    size="page-plain-header"
                    :heading="$t('pages.administrator.outreach_tasks.heading')"
                ></common-page-header>

                <div class="outreach-tasks-filters">
                    <div class="outreach-tasks-filters__status">
                        <label for="filter-status">{{ $t('pages.administrator.outreach_tasks.table.status') }}</label>
                        <select id="filter-status" v-model="filterStatus" @change="onFilterChange">
                            <option value="">All</option>
                            <option value="PENDING">{{ $t('pages.administrator.outreach_tasks.status.PENDING') }}</option>
                            <option value="PROCESSED">{{ $t('pages.administrator.outreach_tasks.status.PROCESSED') }}</option>
                            <option value="CANCELLED">{{ $t('pages.administrator.outreach_tasks.status.CANCELLED') }}</option>
                        </select>
                    </div>
                    <div class="outreach-tasks-filters__date">
                        <label for="filter-from">{{ $t('pages.administrator.outreach_tasks.filters.from_date') }}</label>
                        <input id="filter-from" v-model="filterFromDate" type="date" @change="onFilterChange" />
                    </div>
                    <div class="outreach-tasks-filters__date">
                        <label for="filter-to">{{ $t('pages.administrator.outreach_tasks.filters.to_date') }}</label>
                        <input id="filter-to" v-model="filterToDate" type="date" @change="onFilterChange" />
                    </div>
                </div>

                <div class="outreach-table">
                    <div class="outreach-table__header">
                        <div class="outreach-table__header__item">
                            {{ $t('pages.administrator.outreach_tasks.table.task_id') }}
                        </div>
                        <div class="outreach-table__header__item">
                            {{ $t('pages.administrator.outreach_tasks.table.pool_id') }}
                        </div>
                        <div class="outreach-table__header__item">
                            {{ $t('pages.administrator.outreach_tasks.table.provider') }}
                        </div>
                        <div class="outreach-table__header__item">{{ $t('pages.administrator.outreach_tasks.table.npi') }}</div>
                        <div class="outreach-table__header__item">
                            {{ $t('pages.administrator.outreach_tasks.table.channel') }}
                        </div>
                        <div class="outreach-table__header__item">
                            {{ $t('pages.administrator.outreach_tasks.table.status') }}
                        </div>
                        <div class="outreach-table__header__item">
                            {{ $t('pages.administrator.outreach_tasks.table.scheduled_for') }}
                        </div>
                        <div class="outreach-table__header__item">
                            {{ $t('pages.administrator.outreach_tasks.table.sent_at') }}
                        </div>
                    </div>

                    <div v-if="isLoading" class="outreach-table__loading">
                        <common-loader :isProcessing="isLoading"></common-loader>
                    </div>

                    <div v-else-if="outreachTasks.length === 0" class="outreach-table__empty">
                        <p>{{ $t('pages.administrator.outreach_tasks.table.no_tasks') }}</p>
                    </div>

                    <div v-else class="outreach-table__content">
                        <div v-for="task in outreachTasks" :key="task.id" class="outreach-table__row">
                            <div class="outreach-table__row__item">{{ task.id }}</div>
                            <div class="outreach-table__row__item">
                                <router-link
                                    v-if="task.referralsPoolId"
                                    :to="{ name: 'referrals.made.pool.show', params: { type: 'made', id: task.referralsPoolId } }"
                                    class="outreach-table__pool-link"
                                >
                                    {{ task.referralsPoolId }}
                                </router-link>
                                <span v-else>—</span>
                            </div>
                            <div class="outreach-table__row__item">{{ task.providerName }}</div>
                            <div class="outreach-table__row__item">{{ task.providerNpi || '—' }}</div>
                            <div class="outreach-table__row__item">{{ channelLabel(task.primaryChannel) }}</div>
                            <div class="outreach-table__row__item">{{ statusLabel(task.status) }}</div>
                            <div class="outreach-table__row__item">{{ formatDate(task.scheduledForDateTime) }}</div>
                            <div class="outreach-table__row__item">{{ formatDate(task.closedDateTime) }}</div>
                        </div>
                    </div>
                </div>

                <div v-if="totalPages > 1" class="outreach-pagination">
                    <common-button
                        :isDisabled="currentPage === 1"
                        :label="$t('common.microcopy.previous')"
                        type="secondary"
                        size="small"
                        @click="changePage(currentPage - 1)"
                    ></common-button>
                    <span class="outreach-pagination__info">
                        {{
                            $t('pages.administrator.outreach_tasks.pagination.info', { current: currentPage, total: totalPages })
                        }}
                    </span>
                    <common-button
                        :isDisabled="currentPage === totalPages"
                        :label="$t('common.microcopy.next')"
                        type="secondary"
                        size="small"
                        @click="changePage(currentPage + 1)"
                    ></common-button>
                </div>

                <common-notification ref="commonSuccessNotificationChild" :content="notificationMessage"></common-notification>
            </common-content-spacer>
        </common-page-plain>
    </app-layout>
</template>

<script>
    import AppLayout from 'components/layouts/AppLayout'
    import PageWithTitleMixin from 'mixins/PageWithTitleMixin'
    import PageWithElementsMixin from 'mixins/PageWithElementsMixin'
    import AdministratorAPI from 'api/AdministratorAPI'
    import dayjs from 'dayjs'

    const LIMIT = 20

    export default {
        mixins: [PageWithTitleMixin, PageWithElementsMixin],

        components: {
            AppLayout,
        },

        data() {
            return {
                outreachTasks: [],
                isLoading: false,
                currentPage: 1,
                totalPages: 1,
                filterStatus: '',
                filterFromDate: '',
                filterToDate: '',
                notificationMessage: '',
            }
        },

        methods: {
            onFilterChange() {
                this.currentPage = 1
                this.loadOutreachTasks()
            },

            loadOutreachTasks() {
                this.isLoading = true
                const params = {
                    limit: LIMIT,
                    offset: (this.currentPage - 1) * LIMIT,
                }
                if (this.filterStatus) params.status = this.filterStatus
                if (this.filterFromDate) params.fromDate = this.filterFromDate
                if (this.filterToDate) params.toDate = this.filterToDate

                AdministratorAPI.getOutreachTasks(params)
                    .then((response) => {
                        const data = response.data || response
                        this.outreachTasks = data.items || []
                        this.totalPages = Math.max(1, Math.ceil((data.total || 0) / LIMIT))
                    })
                    .catch(() => {
                        this.notificationMessage = this.$t('pages.administrator.outreach_tasks.notifications.load_failed')
                        this.$refs.commonSuccessNotificationChild.showNotification()
                    })
                    .finally(() => {
                        this.isLoading = false
                    })
            },

            changePage(page) {
                this.currentPage = page
                this.loadOutreachTasks()
            },

            formatDate(date) {
                if (!date) return '—'
                return dayjs(date).format('MMM DD, YYYY HH:mm')
            },

            statusLabel(status) {
                if (!status) return '—'
                const key = `pages.administrator.outreach_tasks.status.${status}`
                const t = this.$t(key)
                return t !== key ? t : status
            },

            channelLabel(channel) {
                if (!channel) return '—'
                const key = `pages.administrator.outreach_tasks.channel.${channel}`
                const t = this.$t(key)
                return t !== key ? t : channel
            },
        },

        mounted() {
            this.loadOutreachTasks()
        },
    }
</script>

<style scoped lang="scss">
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_mixins.scss';

    .outreach-tasks-filters {
        margin-bottom: 1rem;
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 1rem;

        &__status,
        &__date {
            label {
                margin-right: 0.5rem;
            }
            select,
            input[type='date'] {
                padding: 0.25rem 0.5rem;
                border-radius: $border-radius--base;
                border: 1px solid $color--border-base;
            }
        }
    }

    .outreach-table {
        width: 100%;
        border: 1px solid $color--border-base;
        border-radius: $border-radius--base;
        overflow-x: auto;

        &__header {
            display: grid;
            grid-template-columns: 80px 80px 1fr 100px 80px 100px 140px 140px;
            background-color: $color--bg-dropdown;
            padding: 1rem;
            font-weight: bold;
            border-bottom: 1px solid $color--border-base;

            &__item {
                padding: 0.5rem;
            }
        }

        &__loading,
        &__empty {
            padding: 3rem;
            text-align: center;
            color: $color--txt-minor;
        }

        &__content {
            .outreach-table__row {
                display: grid;
                grid-template-columns: 80px 80px 1fr 100px 80px 100px 140px 140px;
                padding: 1rem;
                border-bottom: 1px solid $color--border-base;
                align-items: center;

                &:hover {
                    background-color: $color--bg-dropdown;
                }

                &__item {
                    padding: 0.5rem;
                }
            }
        }
    }

    .outreach-table__pool-link {
        color: $color--accent-1;
        text-decoration: none;

        &:hover {
            text-decoration: underline;
        }
    }

    .outreach-pagination {
        margin-top: 2rem;
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 1rem;

        &__info {
            color: $color--txt-minor;
        }
    }
</style>
